/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Tue May 23 2023
 **/

import { Suspense, useEffect, useRef, useState } from "react";
import { useTheme } from "@emotion/react";

import { EventID, ZIndex } from "@/frameworks/core/commons";
import { SwitchCase } from "@/frameworks/libraries/components";
import { EventHandler } from "@rfice/eventhandler";
import { Divider, Icon, Loader, Row, Typography } from "@/frameworks/designStuff/components";
import { useFileUpload } from "@/frameworks/libraries/hooks";
import withRenderDetect from "@/frameworks/core/utils/withRenderDetect";
import { useFullSizeChatManager, usePopover } from "@/frameworks/core/hooks";
import { useChatDataStore } from "@/modules/chat/store";
import { _T } from "@rfice/language";

import FullSizeThread from "./common/components/FullSizeThread";
import FullSizeDMChat from "./dm/components/FullSizeDMChat";
import { FullSizeSpaceChat } from "./space/components";
import * as S from "./emotion";

function FullSizeChat() {
	const { useFullSizeChatState } = useFullSizeChatManager();
	const fullSizeChatState = useFullSizeChatState();
	const [currentChannelId, setCurrentChannelId] = useState(fullSizeChatState?.channelID || "");
	const [prevChannelID, setPrevChannelID] = useState(fullSizeChatState?.channelID || "");
	const [threadWidth, setThreadWidth] = useState<number>(Math.min((window.innerWidth - 400) * 0.4, 500));
	const threadID = useChatDataStore((state) => state.threadID);
	const popover = usePopover();
	const dividerRef = useRef<HTMLDivElement>(null);

	const { inputRef, drag, removeAll, reset } = useFileUpload({
		id: fullSizeChatState?.channelID || "",
		multiple: true
	});

	const { onDrop, ...rest } = drag.listeners;
	const handleDrop = (ev: React.DragEvent<HTMLLabelElement>) => {
		EventHandler.emit(EventID.DROP_CHAT_FILES, {
			channelID: currentChannelId,
			fileList: [...ev.dataTransfer.files],
			variant: "fullSize"
		});
		onDrop(ev);
		removeAll();
		reset();
	};

	useEffect(() => {
		if (fullSizeChatState?.channelID === prevChannelID) {
			return;
		}

		setPrevChannelID(fullSizeChatState?.channelID || "");
		setCurrentChannelId(fullSizeChatState?.channelID || "");
	}, [fullSizeChatState, prevChannelID]);

	const isInvalidChannel = !fullSizeChatState || !fullSizeChatState?.channelID;

	useEffect(() => {
		popover.hideAll();
	}, []);

	useEffect(() => {
		const divider = dividerRef.current;
		if (!divider || !threadID) return;

		const handleMouseMove = (e: MouseEvent) => {
			e.preventDefault();
			const parentRect = divider.parentElement?.getBoundingClientRect();

			if (parentRect) {
				const { width } = parentRect;
				const childWidth = 400 + width - e.clientX;
				setThreadWidth(childWidth);
			}
		};

		const handleMouseUp = () => {
			document.removeEventListener("mousemove", handleMouseMove);
			document.removeEventListener("mouseup", handleMouseUp);
		};

		const handleMouseDown = (e: MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			document.addEventListener("mousemove", handleMouseMove);
			document.addEventListener("mouseup", handleMouseUp);
		};

		divider.addEventListener("mousedown", handleMouseDown);

		return () => {
			divider.removeEventListener("mousedown", handleMouseDown);
			document.removeEventListener("mousemove", handleMouseMove);
			document.removeEventListener("mouseup", handleMouseUp);
		};
	}, [threadID]);

	if (isInvalidChannel || fullSizeChatState.channelID !== prevChannelID) return <Loader />;
	return (
		<SwitchCase
			condition={fullSizeChatState?.chatType}
			cases={{
				DM: (
					<S.FullSizeChatLayout autoFocus tabIndex={0} onDrop={handleDrop} {...rest}>
						<Row style={{ width: "100%", height: "100%" }}>
							<S.ChatLayout hasThread={Boolean(threadID)} width={threadWidth}>
								<Suspense fallback={<Loader />}>
									{drag.isDragging && <IsDragging />}
									<input
										type="file"
										id="file-upload"
										multiple={true}
										ref={inputRef}
										style={{ display: "none" }}
									/>
									<FullSizeDMChat
										channelID={fullSizeChatState?.channelID}
										activityMessageID={fullSizeChatState?.activityMessageID}
									/>
								</Suspense>
							</S.ChatLayout>
							{threadID && <Divider type="vertical" ref={dividerRef} />}
							{threadID && (
								<S.ThreadLayout width={threadWidth}>
									<FullSizeThread
										channelID={fullSizeChatState?.channelID}
										threadID={threadID}
										type={"DM"}
									/>
								</S.ThreadLayout>
							)}
						</Row>
					</S.FullSizeChatLayout>
				),
				Space: (
					<S.FullSizeChatLayout autoFocus tabIndex={0} onDrop={handleDrop} {...rest}>
						<Row style={{ width: "100%", height: "100%" }}>
							<S.ChatLayout hasThread={Boolean(threadID)} width={threadWidth}>
								<Suspense fallback={<Loader />}>
									{drag.isDragging && <IsDragging />}
									<input
										type="file"
										id="file-upload"
										multiple={true}
										ref={inputRef}
										style={{ display: "none" }}
									/>
									<FullSizeSpaceChat
										spaceID={fullSizeChatState?.spaceID || ""}
										roomID={fullSizeChatState?.channelID}
										handleChangeSpace={(id) => {
											setCurrentChannelId(id);
										}}
										activityMessageID={fullSizeChatState?.activityMessageID}
									/>
								</Suspense>
							</S.ChatLayout>
							{threadID && (
								<>
									<Divider type="vertical" ref={dividerRef} />
									<S.ThreadLayout width={threadWidth}>
										<FullSizeThread
											channelID={fullSizeChatState?.channelID}
											spaceID={fullSizeChatState?.spaceID || ""}
											threadID={threadID}
											type={"Space"}
										/>
									</S.ThreadLayout>
								</>
							)}
						</Row>
					</S.FullSizeChatLayout>
				)
			}}
			defaultCase={<Loader />}
		/>
	);
}

function IsDragging() {
	const theme = useTheme();
	return (
		<Row
			verticalAlign="center"
			horizonAlign="center"
			gap={12}
			css={{
				position: "absolute",
				top: 0,
				left: 0,
				width: "100%",
				height: "100%",
				background: theme.colors.secondary_80_16,
				backdropFilter: "blur(14px)",
				zIndex: ZIndex.FULL_SIZE_CHAT + 1
			}}
		>
			<Typography typoColor="neutral_80_100" typoSize="Title1">
				{_T("파일 업로드")}
			</Typography>
			<Icon name="Upload24" fill={theme.colors.neutral_80_100} size={24} />
		</Row>
	);
}

FullSizeChat.displayName = "FullSizeChat";
export default withRenderDetect(FullSizeChat);
